import { cva } from "class-variance-authority";
import { forwardRef, type HTMLProps, type ReactNode } from "react";
import type { UseFormRegister } from "react-hook-form";

import { Typography } from "@/components/ui/typography";
import { cn } from "@/lib/utils";

const textInputContainerStyles = cva("flex items-center border border-solid rounded-lg px-4 h-11", {
  variants: {
    error: {
      true: "border-error-300",
      false: "border-gray-300 focus-within:border-primary-400",
    },
    disabled: {
      true: "bg-gray-50 cursor-not-allowed",
    },
  },
  defaultVariants: {
    error: false,
    disabled: false,
  },
});

interface TextInputProps extends Omit<HTMLProps<HTMLInputElement>, "disabled"> {
  label?: string;
  name?: string;
  error?: string;
  register?: UseFormRegister<any>;
  required?: boolean;
  disabled?: boolean;
  className?: string;
  startAdornment?: ReactNode;
  endAdornment?: ReactNode;
  inputWrapperClassName?: string;
}
const TextInput = forwardRef<HTMLInputElement, TextInputProps>(
  (
    {
      label,
      className,
      startAdornment,
      inputWrapperClassName,
      endAdornment,
      value,
      disabled,
      error,
      ...other
    }: TextInputProps,
    ref
  ) => {
    // Keep native input controlled value safe when upstream state provides null.
    const safeValue = value === null ? "" : value;

    return (
      <div className={cn("flex flex-col gap-[6px]", className)}>
        {label && (
          <label className="text-sm text-gray-700">
            <Typography variant="text-small" weight="medium">
              {label}
            </Typography>
          </label>
        )}
        <div className={cn(textInputContainerStyles({ error: !!error, disabled }), inputWrapperClassName)}>
          {startAdornment && startAdornment}
          <input
            disabled={!!disabled}
            value={safeValue}
            className="w-full bg-transparent  outline-hidden"
            ref={ref}
            {...other}
          />
          {endAdornment && endAdornment}
        </div>
        {error && (
          <Typography className="text-error-500" variant="text-small">
            {error}
          </Typography>
        )}
      </div>
    );
  }
);

TextInput.displayName = "TextInput";

export { TextInput };
